import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FiCheckCircle, FiChevronRight, FiExternalLink, FiEyeOff, FiGlobe, FiHelpCircle, FiMap, FiRefreshCw } from "react-icons/fi";
import { usePageHeader } from "../hooks/usePageHeader";
import { LoadingSpinner } from "../Components/LoadingSpinner";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

interface SitemapSectionCount {
	section: string;
	count: number;
}

interface SitemapSectionUrlsResponse {
	available: boolean;
	target: string;
	section: string;
	siteUrl?: string | null;
	count: number;
	urls: string[];
}

interface SitemapTargetSummary {
	target: "preview" | "production" | string;
	siteUrl?: string | null;
	totalUrls: number;
	sections: SitemapSectionCount[];
	robotsChecked: boolean;
	robotsIndexable?: boolean | null;
	error?: string | null;
}

interface SitemapSummaryResponse {
	available: boolean;
	targets: SitemapTargetSummary[];
	languages: string[];
}

const TARGET_LABELS: Record<string, string> = {
	production: "Production"
};

/** Pastille d'état robots.txt : ce que le site annonce RÉELLEMENT aux moteurs de recherche.
 *  Seule la production est affichée sur cette page : indexable = attendu, sinon alerte. */
const RobotsPill: React.FC<{ summary: SitemapTargetSummary }> = ({ summary }) => {
	if (!summary.robotsChecked) {
		return (
			<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500" title="robots.txt injoignable depuis l'admin">
				<FiHelpCircle className="h-3.5 w-3.5" /> Vérification impossible
			</span>
		);
	}
	if (summary.robotsIndexable) {
		return (
			<span
				className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"
				title="robots.txt autorise l'indexation : attendu en production"
			>
				<FiCheckCircle className="h-3.5 w-3.5" /> Indexé par les moteurs
			</span>
		);
	}
	return (
		<span
			className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700"
			title="La production s'annonce NON indexable : vérifier ALLOW_ROBOT"
		>
			<FiEyeOff className="h-3.5 w-3.5" /> Non indexé
		</span>
	);
};

/** Un sitemap enfant (une section) : ligne extensible qui, à l'ouverture, charge et liste SES liens. */
const ChildSitemapRow: React.FC<{ target: string; section: SitemapSectionCount; siteUrl?: string | null }> = ({ target, section, siteUrl }) => {
	const [open, setOpen] = useState(false);
	const [urls, setUrls] = useState<string[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [filter, setFilter] = useState("");

	const toggle = async () => {
		const next = !open;
		setOpen(next);
		if (next && urls === null) {
			setLoading(true);
			try {
				const res = await axios.get<SitemapSectionUrlsResponse>(
					`${apiBase}/api/sitemaps/${encodeURIComponent(target)}/section/${encodeURIComponent(section.section)}`,
					{ withCredentials: true }
				);
				setUrls(res.data.urls ?? []);
			} catch {
				setUrls([]);
			} finally {
				setLoading(false);
			}
		}
	};

	const filtered = useMemo(() => {
		const list = urls ?? [];
		const q = filter.trim().toLowerCase();
		return q ? list.filter((u) => u.toLowerCase().includes(q)) : list;
	}, [urls, filter]);

	return (
		<div className="border-b border-slate-100 last:border-0">
			<div className="flex items-center gap-2 px-4 py-2.5">
				<button type="button" onClick={toggle} className="flex flex-1 items-center gap-2 text-left">
					<FiChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
					<span className="font-medium text-slate-800">/{section.section}</span>
					<span className="text-xs text-slate-400">{section.count.toLocaleString("fr-FR")} lien{section.count > 1 ? "s" : ""}</span>
				</button>
				{siteUrl && (
					<a
						href={`${siteUrl}/sitemap/${encodeURIComponent(section.section)}.xml`}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"
						title={`Ouvrir /sitemap/${section.section}.xml`}
					>
						<FiExternalLink className="h-3 w-3" /> XML
					</a>
				)}
			</div>
			{open && (
				<div className="px-4 pb-3">
					{loading ? (
						<div className="flex items-center gap-2 py-2 text-xs text-slate-400"><LoadingSpinner size="sm" /> Chargement des liens…</div>
					) : (
						<>
							{(urls?.length ?? 0) > 8 && (
								<input
									value={filter}
									onChange={(e) => setFilter(e.target.value)}
									placeholder="Filtrer les liens…"
									className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:border-sky-400 focus:outline-none"
								/>
							)}
							<ul className="max-h-72 space-y-0.5 overflow-auto rounded-lg border border-slate-100 bg-slate-50 p-2">
								{filtered.map((u) => (
									<li key={u}>
										<a
											href={siteUrl ? `${siteUrl}${u}` : u}
											target="_blank"
											rel="noopener noreferrer"
											className="block truncate rounded px-2 py-1 text-xs text-slate-600 hover:bg-white hover:text-sky-700"
											title={u}
										>
											{u}
										</a>
									</li>
								))}
								{filtered.length === 0 && <li className="px-2 py-1 text-xs text-slate-400">Aucun lien.</li>}
							</ul>
						</>
					)}
				</div>
			)}
		</div>
	);
};

/** La liste des sitemaps enfants d'UNE cible : un sitemap par section, chacun dépliable pour voir ses liens. */
const TargetSitemaps: React.FC<{ t: SitemapTargetSummary }> = ({ t }) => {
	const sections = useMemo(
		() => [...(t.sections ?? [])].sort((a, b) => b.count - a.count || a.section.localeCompare(b.section)),
		[t.sections]
	);
	if (sections.length === 0) return null;
	return (
		<section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
			<header className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
				<h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Sitemaps : {TARGET_LABELS[t.target] ?? t.target}</h2>
				<span className="text-xs text-slate-400">{sections.length} sitemap{sections.length > 1 ? "s" : ""} enfant{sections.length > 1 ? "s" : ""}</span>
			</header>
			<div>
				{sections.map((s) => (
					<ChildSitemapRow key={s.section} target={t.target} section={s} siteUrl={t.siteUrl} />
				))}
			</div>
		</section>
	);
};

/**
 * Visualiseur des sitemaps publics (lecture seule) : pour chaque cible, le nombre d'URLs
 * réellement présentes dans la source du sitemap (l'index de recherche D1 : la même table que le
 * XML public), l'état robots.txt servi en direct, le lien vers le sitemap public, et la
 * répartition par section. Les langues listées sont les alternances hreflang déclarées par le
 * sitemap pour chaque URL.
 */
const Sitemaps: React.FC = () => {
	usePageHeader({ title: "Sitemaps" });
	const [data, setData] = useState<SitemapSummaryResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const load = async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await axios.get<SitemapSummaryResponse>(`${apiBase}/api/sitemaps/summary`, { withCredentials: true });
			setData(res.data);
		} catch (err) {
			console.error(err);
			setError("Impossible de charger le résumé des sitemaps.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, []);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
						<FiMap className="h-5 w-5" />
					</span>
					<div>
						<h1 className="text-2xl font-semibold text-slate-900">Sitemaps</h1>
						<p className="text-sm text-slate-500">
							Le plan de site XML est généré automatiquement par le site public à partir du contenu publié : voici ce qu'il contient réellement.
						</p>
					</div>
					<button
						type="button"
						onClick={load}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						<FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
					</button>
				</div>

				{error && (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
				)}

				{loading && !data ? (
					<div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-10 text-sm text-slate-400">
						<LoadingSpinner size="sm" /> Chargement du résumé…
					</div>
				) : data && (
					<>
						{!data.available && (
							<div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
								La source du sitemap (index D1) n'est pas configurée sur cette instance : les compteurs sont indisponibles.
							</div>
						)}

						<div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
							{data.targets.filter((t) => t.target === "production").map((t) => (
								<section key={t.target} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
									<div className="flex flex-wrap items-center justify-between gap-2">
										<h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
											{TARGET_LABELS[t.target] ?? t.target}
										</h2>
										<RobotsPill summary={t} />
									</div>
									<p className="mt-3 text-3xl font-bold text-slate-900">
										{t.error ? "-" : t.totalUrls.toLocaleString("fr-FR")}
										<span className="ml-2 text-sm font-medium text-slate-400">URL{t.totalUrls > 1 ? "s" : ""} dans le sitemap</span>
									</p>
									{t.error && <p className="mt-1 text-xs text-rose-600">{t.error}</p>}
									{t.siteUrl && (
										<a
											href={`${t.siteUrl}/sitemap.xml`}
											target="_blank"
											rel="noopener noreferrer"
											className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:underline"
										>
											<FiExternalLink className="h-3.5 w-3.5" /> Ouvrir sitemap.xml
										</a>
									)}
								</section>
							))}
						</div>

						{data.languages.length > 0 && (
							<div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3.5 text-sm text-slate-600 shadow-sm">
								<FiGlobe className="h-4 w-4 text-slate-400" />
								<span className="font-medium">Langues déclarées (hreflang) :</span>
								{data.languages.map((code) => (
									<span key={code} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold uppercase text-slate-600">
										{code}
									</span>
								))}
								<span className="text-xs text-slate-400">chaque URL du sitemap déclare une alternative par langue activée.</span>
							</div>
						)}

						{data.available && data.targets.some((t) => t.target === "production" && (t.sections?.length ?? 0) > 0) && (
							<div className="space-y-2">
								<p className="px-1 text-sm text-slate-500">
									La production expose un <span className="font-medium text-slate-700">index de sitemaps</span> : un sitemap enfant par section.
									Dépliez une section pour voir exactement quels liens en proviennent.
								</p>
								<div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))" }}>
									{data.targets.filter((t) => t.target === "production").map((t) => (
										<TargetSitemaps key={t.target} t={t} />
									))}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default Sitemaps;
